import "./MatchPatient.styles.scss";
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PatientsDropdown, GroupNameTag, Button, SidePanel } from "components";
import {
  useGetGroupUsers,
  useAssignSubmissionToPatient,
  useCurrentUser,
  useDeletePracticeSubmission,
} from "utils/hooks";
import useOrganizationSettings, {
  getPatientWord,
  getPatientWordCapitalized,
} from "utils/hooks/organizations/useOrganizationSettings";
import DeleteModal from "pages/AppPages/Shared/DeleteModal";
import { User, AssignmentSubmission, Patient } from "types";
import { getPatientNameEmail } from "pages/AppPages/TherapistPages/Inbox/utils/helpers";
import { GROUP_USER_ROLES } from "utils/constants";
import AddPatientsForm from "pages/AppPages/TherapistPages/Patients/components/AddPatientsForm";
import ReassignPatient from "pages/AppPages/TherapistPages/Patients/components/PatientActionsMenu/ReassignPatient";

type MatchPatientProps = {
  submission: AssignmentSubmission | undefined;
  onClose: (() => void) | undefined;
};

const MatchPatient = ({ submission, onClose }: MatchPatientProps) => {
  const history = useNavigate();
  const { t } = useTranslation(["common"]);
  const { settings: orgSettings } = useOrganizationSettings();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [edit, setEdit] = useState(submission?.userId ? false : true);
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [addingPatient, setAddingPatient] = useState(false);

  const { groupUsers } = useGetGroupUsers({
    groupId: submission?.groupId || null,
  });
  const { assignSubmissionToPatient, loading } = useAssignSubmissionToPatient({
    submissionId: submission?.uuid,
  });
  const { deletePracticeSubmission, deleting } = useDeletePracticeSubmission({
    submissionId: submission?.uuid,
  });
  const { user } = useCurrentUser();

  const groupPatients = groupUsers?.filter(
    (user: User) => user.role === GROUP_USER_ROLES.MEMBER
  );
  const isPracticeOwner = user?.role === "Owner";

  useEffect(() => setEdit(!Boolean(submission?.userId)), [submission]);

  const handleAssignSubmissionToPatient = (e: any) => {
    if (!e?.value) {
      toast.warn(
        t("patients.choose_patient_warn", {
          patientWord: getPatientWord(orgSettings),
        })
      );
      return;
    }
    if (loading) return;
    assignSubmissionToPatient({ patientId: e?.value });
  };

  const handleDeleteSubmission = () => {
    setOpenDeleteModal(false);
    deletePracticeSubmission();
    onClose && onClose();
  };

  const handleAddPatient = async () => {
    if (!submission) return;
    setShowAddPatient(true);
  };

  const patientNameEmail = useMemo(() => {
    if (!submission) return null;
    const nameEmail = getPatientNameEmail(submission.userId, groupPatients);
    return (
      nameEmail || { name: submission?.userName, email: submission?.userEmail }
    );
  }, [groupPatients, submission]);

  const fullNameFromSubmission = submission?.userName?.split(" ");

  const handleNewPatient = async (newPatient: Patient) => {
    if (loading) return;
    setAddingPatient(true);
    await assignSubmissionToPatient({ patientId: newPatient.uuid });
    setAddingPatient(false);
    history(`/patients/${newPatient.slug}/workbook`);
  };

  if (!isPracticeOwner) return null;
  return (
    <div
      className={`match-patient ${
        submission?.isWideData ? "match-patient__flex-space" : ""
      }`}
    >
      {!submission?.userId || edit ? (
        <div className="match-patient__input">
          <PatientsDropdown
            label={t("inbox.match_patient_label", {
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            placeholder={t("calls_to_action.choose_label")}
            patientsList={groupPatients}
            onChange={handleAssignSubmissionToPatient}
          />
          {submission?.groupName && (
            <GroupNameTag groupName={submission.groupName} />
          )}
        </div>
      ) : (
        <div className="match-patient__name-email">
          {patientNameEmail?.name && (
            <div className="name">{patientNameEmail.name}</div>
          )}
          <div className="email">{patientNameEmail?.email}</div>
        </div>
      )}
      <div className="match-patient__buttons">
        <Button
          value={t("common_labels.edit")}
          name="match-patient-edit-btn"
          extraClass="assignment-card_btn"
          icon="EditIconBold"
          isLoading={loading}
          onClick={() => setEdit(!edit)}
        />
        {submission?.userId && (
          <div className="match-patient__button">
            <Button
              value={t("calls_to_action.assign")}
              name="match-patient-assign-btn"
              extraClass="assignment-card_btn"
              icon="UserChecked"
              onClick={() => setShowAssignPanel(!showAssignPanel)}
            />
          </div>
        )}
        {!submission?.userId && (
          <Button
            value={t("patients.add_patient_button", {
              patientWord: getPatientWord(orgSettings),
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            name="match-patient-add-btn"
            extraClass="assignment-card_btn"
            icon="AddPatientBold"
            isLoading={addingPatient}
            onClick={handleAddPatient}
          />
        )}
        <Button
          value={t("common_labels.delete")}
          name="match-patient-delete-btn"
          extraClass="assignment-card_btn"
          icon="TrashBold"
          isLoading={deleting}
          onClick={() => setOpenDeleteModal(true)}
        />
      </div>
      <DeleteModal
        isOpen={openDeleteModal}
        loading={deleting}
        fullScreen={true}
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={handleDeleteSubmission}
      />
      <SidePanel
        title={t("common_labels.add_patient_form_title", {
          patientWordCapitalized: getPatientWordCapitalized(orgSettings),
        })}
        isVisible={showAddPatient}
        onClose={() => setShowAddPatient(false)}
      >
        <AddPatientsForm
          onSuccess={() => setShowAddPatient(false)}
          prefilledData={{
            email: submission?.userEmail || undefined,
            firstName: fullNameFromSubmission?.[0],
            lastName: fullNameFromSubmission?.[1],
          }}
          onNewPatient={handleNewPatient}
          redirectToNewPatient={false}
        />
      </SidePanel>
      <SidePanel
        title={t("patients.reassign_patient_title", {
          patientWordCapitalized: getPatientWordCapitalized(orgSettings),
        })}
        isVisible={showAssignPanel}
        onClose={() => setShowAssignPanel(false)}
      >
        <ReassignPatient
          submission={submission}
          onClose={() => setShowAssignPanel(false)}
        />
      </SidePanel>
    </div>
  );
};

export default MatchPatient;
